import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, MapPin, Calendar, User, X } from "lucide-react";
import { useGlobalSearch, type SearchResultItem } from "@/hooks/useGlobalSearch";
import { getPlaceCoverUrlCached } from "@/hooks/usePlaceCoverUrl";
import { track } from "@/lib/analytics";

const ITEM_KIND_MAP: Record<SearchResultItem["kind"], "place" | "activity" | "producer"> = {
  place: "place",
  activity: "activity",
  host: "producer",
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const GROUP_LABELS: Record<string, string> = {
  places: "Platser",
  activities: "Aktiviteter",
  hosts: "Värdar",
};

export function GlobalSearchOverlay({ open, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const navigate = useNavigate();
  const { results, allItems, hasResults, isLoading, isActive } = useGlobalSearch(query);
  const lastTrackedQuery = useRef("");

  // Track search_performed when results settle for a query
  useEffect(() => {
    const trimmed = query.trim();
    if (!isActive || isLoading || !trimmed || trimmed === lastTrackedQuery.current) return;
    lastTrackedQuery.current = trimmed;

    const resultCount = allItems.length;
    track("search_performed", {
      query: trimmed,
      query_redacted: false, // sanitized centrally in track()
      result_count: resultCount,
      filters: null,
      geo_mode: null,
      debounced: false as const,
    }, "explore");

    if (resultCount === 0) {
      track("search_no_results", {
        query: trimmed,
        query_redacted: false,
        filters: null,
        geo_mode: null,
      }, "explore");
    }
  }, [isActive, isLoading, query, allItems.length]);

  // Reset on open/close, lock body scroll
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      setQuery("");
      setActiveIndex(0);
      lastTrackedQuery.current = "";
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      document.body.style.overflow = "";
      if (triggerRef.current && triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const trackAndNavigate = useCallback(
    (item: SearchResultItem, rank: number) => {
      track("search_result_clicked", {
        query: query.trim(),
        query_redacted: false,
        item_type: ITEM_KIND_MAP[item.kind],
        item_id: item.id,
        rank,
        href: item.href,
      }, "explore");
      onClose();
      navigate(item.href);
    },
    [navigate, onClose, query]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && allItems.length > 0) {
      e.preventDefault();
      const item = allItems[activeIndex] || allItems[0];
      trackAndNavigate(item, activeIndex + 1);
    }
  };

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [allItems.length]);

  if (!open) return null;

  // Build ordered flat list for tracking active index
  let flatIndex = 0;

  const renderGroup = (key: "places" | "activities" | "hosts") => {
    const items = results[key];
    if (items.length === 0) return null;
    const label = GROUP_LABELS[key];
    return (
      <div key={key} data-testid={`search-group-${key}`}>
        <p className="px-4 pt-4 pb-1.5 text-[0.6875rem] font-medium uppercase tracking-widest text-muted-foreground/50 select-none">
          {label}
        </p>
        {items.map((item) => {
          const idx = flatIndex++;
          return (
            <ResultRow
              key={item.id}
              item={item}
              isActive={idx === activeIndex}
              onClick={() => trackAndNavigate(item, idx + 1)}
              onMouseEnter={() => setActiveIndex(idx)}
            />
          );
        })}
      </div>
    );
  };

  // We need to compute flatIndex before render, so reset it
  flatIndex = 0;

  return (
    <div
      data-testid="search-overlay"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] sm:pt-[16vh] px-4"
    >
      {/* Backdrop — tapping closes */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel — clicks stay inside */}
      <div
        className="relative w-full max-w-2xl rounded-xl bg-background/95 backdrop-blur-md shadow-2xl border border-border/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input + close button */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/10">
          <Search className="h-5 w-5 text-muted-foreground/40 shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Sök plats, aktivitet eller värd"
            className="flex-1 bg-transparent text-base sm:text-lg text-foreground placeholder:text-muted-foreground/40 outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng sök"
            className="shrink-0 p-1.5 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results */}
        {isActive && (
          <div className="max-h-[50vh] overflow-y-auto overscroll-contain">
            {isLoading && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground/40">
                Söker…
              </div>
            )}
            {!isLoading && !hasResults && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground/40">
                Inga resultat
              </div>
            )}
            {!isLoading && hasResults && (
              <div className="pb-2">
                {renderGroup("places")}
                {renderGroup("activities")}
                {renderGroup("hosts")}
              </div>
            )}
          </div>
        )}

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-border/10 flex items-center justify-between">
          <span className="text-[0.6875rem] text-muted-foreground/30 select-none sm:hidden">
            Tryck utanför för att stänga
          </span>
          <span className="text-[0.6875rem] text-muted-foreground/30 select-none hidden sm:inline">
            Esc för att stänga
          </span>
        </div>
      </div>
    </div>
  );
}

const FALLBACK_ICONS = {
  place: MapPin,
  activity: Calendar,
  host: User,
} as const;

function ResultRow({
  item,
  isActive,
  onClick,
  onMouseEnter,
}: {
  item: SearchResultItem;
  isActive: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  const thumbUrl = item.kind === "place" ? getPlaceCoverUrlCached(item.imageUrl) : null;
  const FallbackIcon = FALLBACK_ICONS[item.kind];

  return (
    <button
      data-testid="search-result-row"
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors duration-100 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
        isActive ? "bg-muted/50" : "hover:bg-muted/30"
      }`}
    >
      {/* Thumbnail */}
      <div className="shrink-0 w-10 h-10 rounded-md overflow-hidden bg-muted flex items-center justify-center">
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <FallbackIcon className="h-4 w-4 text-muted-foreground/40" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground/90 truncate">{item.title}</p>
        {item.subtitle && (
          <p className="text-xs text-muted-foreground/50 truncate">{item.subtitle}</p>
        )}
      </div>
      <ArrowRight
        className={`h-3.5 w-3.5 text-muted-foreground/30 shrink-0 transition-opacity duration-150 ${
          isActive ? "opacity-100" : "opacity-0"
        }`}
      />
    </button>
  );
}
